import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminInformationCreateForm from '@/components/organisms/AdminInformationCreateForm';
import { useAdminInformationCreate } from '@/hooks/admin/useAdminInformationCreate';
import { ADMIN_ROUTES } from '@/constants/routes';
import {
  InformationCreateFormData,
  resolveServiceType,
  mergeDateTime,
} from '@/schemas/informationSchema';
import { AppBreadcrumb, AppCard, AppContainer } from '@/components/atoms';
import { AdminInformationPreviewModal } from '@/components/organisms';
import type { InformationPreviewData } from '@/components/organisms';

const SiteAdminInformationCreatePage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();
  const createMutation = useAdminInformationCreate();
  const [previewData, setPreviewData] = useState<InformationPreviewData | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleSubmit = useCallback(
    (data: InformationCreateFormData) => {
      if (!site) return;

      createMutation.mutate(
        {
          siteType: site,
          data: {
            title: data.title,
            information_date: data.information_date,
            contents: data.contents ?? null,
            display_from: mergeDateTime(data.display_from_date, data.display_from_time),
            display_to: mergeDateTime(data.display_to_date, data.display_to_time),
            is_display: data.is_display,
            service_type: resolveServiceType(data.service_type_a2, data.service_type_r2),
            tenant_ids: data.tenant_ids,
            clinic_ids: data.clinic_ids ?? [],
          },
        },
        {
          onSuccess: () => {
            navigate(ADMIN_ROUTES.INFORMATIONS.LIST(site));
          },
        }
      );
    },
    [site, createMutation, navigate]
  );

  const handlePreview = useCallback(
    (data: InformationPreviewData) => {
      setPreviewData(data);
      setIsPreviewOpen(true);
    },
    []
  );

  if (!site) return null;

  const breadcrumbItems = [
    { title: 'お知らせマスタ', href: ADMIN_ROUTES.INFORMATIONS.LIST(site) },
    { title: '新規' },
  ];

  return (
    <>
      <AppContainer className="py-6 space-y-6">
        <AppBreadcrumb items={breadcrumbItems} />
        <AppCard title="お知らせマスタ作成">
          <AdminInformationCreateForm
            siteType={site}
            onSubmit={handleSubmit}
            isSubmitting={createMutation.isPending}
            onPreview={handlePreview}
          />
        </AppCard>
      </AppContainer>
      <AdminInformationPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        data={previewData}
      />
    </>
  );
};

export default SiteAdminInformationCreatePage;
